import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import { cn } from '@/core/lib/utils';

export interface PillRadioGroupOption {
  value: string;
  label: string;
}

interface PillRadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  options: PillRadioGroupOption[];
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
}

/** Single-select pill/segmented button group, built on Radix's `RadioGroup` for keyboard nav + a11y. */
export function PillRadioGroup({
  value,
  onValueChange,
  options,
  disabled,
  id,
  'aria-invalid': ariaInvalid,
  'data-testid': testId,
}: PillRadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      id={id}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-border bg-muted p-1"
      data-testid={testId}
      aria-invalid={ariaInvalid}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          data-testid={testId ? `${testId}-option-${option.value}` : undefined}
          className={cn(
            'rounded-md border border-transparent px-4 py-1.5 text-sm transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[state=unchecked]:text-muted-foreground data-[state=unchecked]:hover:text-foreground',
            'data-[state=checked]:border-border data-[state=checked]:bg-background data-[state=checked]:font-medium data-[state=checked]:text-primary data-[state=checked]:shadow-sm',
          )}
        >
          {option.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
